import { motion } from "framer-motion";
import { Globe, Target, Heart, Sparkles, Award, Users } from "lucide-react";

export default function About() {
  return (
    <div className="relative">
      <div className="orb orb-blue" style={{ width: 380, height: 380, top: 80, right: -100 }} />
      <div className="orb orb-cyan" style={{ width: 320, height: 320, top: 600, left: -80 }} />

      {/* HERO */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-5">
              <Sparkles className="w-3 h-3" /> ABOUT HAPPY JOURNEY 4 U
            </div>
            <h1 className="font-display font-bold text-5xl lg:text-8xl text-[#0A1A3A] leading-[0.95] max-w-5xl">
              A decade of crafting <span className="font-serif-italic font-medium text-gradient-blue">unforgettable</span> journeys.
            </h1>
            <p className="mt-7 max-w-2xl text-lg lg:text-xl text-slate-600 font-serif-italic leading-relaxed">
              Happy Journey 4 U is a family-run travel atelier founded by <span className="font-semibold not-italic text-[#0A1A3A]">Lucky Jeswani</span>.
              From the serene Ganga ghats of Haridwar to the bustling souks of Dubai, we curate trips that feel intentional and deeply personal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="relative py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative h-[520px]">
            <img src="https://images.pexels.com/photos/1051073/pexels-photo-1051073.jpeg" className="absolute top-0 left-0 w-[70%] h-[60%] img-notched-tl object-cover shadow-2xl shadow-blue-900/20" alt="" />
            <img src="https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg" className="absolute bottom-0 right-0 w-[55%] h-[50%] img-notched-br object-cover shadow-2xl shadow-blue-900/20" alt="" />
            <div className="absolute top-[45%] right-[5%] glass-strong p-5 rounded-2xl">
              <div className="text-[10px] tracking-[0.2em] text-slate-600">FOUNDER</div>
              <div className="font-display font-bold text-xl text-[#0A1A3A] mt-1">Lucky Jeswani</div>
              <div className="text-xs text-slate-500 font-serif-italic">10+ years of curating travel</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— THE STORY</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-[#0A1A3A] leading-tight">
              Where every trip is <span className="font-serif-italic font-medium text-gradient-blue">treated like our own</span>.
            </h2>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed text-lg font-serif-italic">
              <p>
                Lucky ji has been arranging travel for Indian families for over a decade. With deep relationships
                with hotels, drivers, and local guides across India and abroad, he ensures every trip is smooth,
                safe, and full of joyful moments.
              </p>
              <p>
                His philosophy is simple: <em className="text-[#1D4ED8] not-italic font-semibold">"Aapka safar humari zimmedaari."</em> Whether it's a sacred Char Dham
                Yatra, a Kashmir honeymoon, or a corporate group to Bali — we treat your journey like our own.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="relative py-16 lg:py-24 aurora-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="glass p-9">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#1D4ED8] flex items-center justify-center shadow-lg">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="mt-6 font-display font-bold text-3xl text-[#0A1A3A]">Our Mission</h3>
            <p className="mt-3 text-slate-700 text-lg font-serif-italic leading-relaxed">
              To make travel accessible, stress-free, and deeply joyful — by curating itineraries that match every traveler's pace and dreams.
            </p>
          </div>
          <div className="glass p-9">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#22D3EE] flex items-center justify-center shadow-lg">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <h3 className="mt-6 font-display font-bold text-3xl text-[#0A1A3A]">Our Vision</h3>
            <p className="mt-3 text-slate-700 text-lg font-serif-italic leading-relaxed">
              To be India's most trusted travel partner for families, honeymooners, pilgrims, and adventure seekers.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-strong p-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: "10+", l: "Years", icon: Award },
              { v: "5K+", l: "Travelers", icon: Users },
              { v: "50+", l: "Destinations", icon: Globe },
              { v: "4.9★", l: "Rated", icon: Heart },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`text-center ${i > 0 ? "md:border-l border-white/60" : ""}`}
              >
                <s.icon className="w-6 h-6 mx-auto text-[#1D4ED8] mb-2 opacity-80" />
                <div className="font-display font-bold text-4xl lg:text-5xl text-gradient-blue">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-600 mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
